import { createRouter, createWebHistory } from 'vue-router'
import AuthView from './views/AuthView.vue'
import HomeView from './views/HomeView.vue'
import ProfileView from './views/ProfileView.vue'
import QuickStartView from './views/QuickStartView.vue'
import PiecesView from './views/PiecesView.vue'
import ClassroomView from './views/ClassroomView.vue'
import ClassroomLobbyView from './views/ClassroomLobbyView.vue'
import ClassroomGameView from './views/ClassroomGameView.vue'
import { getCurrentUser } from './composables/useSupabaseAuth'
import type { RouteLocationNormalizedLoaded, RouteLocationRaw } from 'vue-router'
import * as Sentry from '@sentry/vue'

// Flag to track initial page load
let isInitialPageLoad = true;

const classroomGameBackTarget = (to: RouteLocationNormalizedLoaded): RouteLocationRaw => ({
  name: 'classroom-lobby',
  params: { code: String(to.params.code ?? '') },
});

// A piece reached from a lesson — uploaded there, or opened from its list —
// returns to that lesson, not the piece list.
const pieceBackTarget = (to: RouteLocationNormalizedLoaded): RouteLocationRaw =>
  typeof to.query.lessonId === 'string'
    ? { name: 'admin-lesson-edit', params: { lessonId: to.query.lessonId } }
    : { name: 'admin-pieces' };

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "home",
      component: HomeView,
      meta: {transition: 'slide-right'}
    },
    {
      path: "/quickstart",
      name: "quickstart",
      component: QuickStartView,
    },
    {
      path: "/auth",
      name: "auth",
      component: AuthView,
    },
    {
      path: "/pieces",
      name: "pieces",
      component: PiecesView,
      meta: { requiresAuth: true, backTo: { name: 'home' } }
    },
    {
      path: "/piece/:pieceId",
      name: "piece-game",
      component: () => import('./views/PieceGameView.vue'),
      meta: { requiresAuth: true, backTo: { name: 'pieces' } }
    },
    {
      path: "/profile",
      name: "profile",
      component: ProfileView,
      meta: { requiresAuth: true }
    },
    {
      path: "/settings",
      name: "settings",
      component: () => import('./views/SettingsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: "/settings/calibration",
      name: "settings-calibration",
      component: () => import('./views/TimeCalibrationView.vue'),
      meta: { requiresAuth: true, backTo: { name: 'settings' } }
    },
    {
      path: "/settings/midi",
      name: "settings-midi",
      component: () => import('./views/ConfigureMidiNoteMappingView.vue'),
      meta: { requiresAuth: true, backTo: { name: 'settings' } }
    },
    {
      path: "/settings/:pathMatch(.*)*",
      redirect: { name: 'settings' },
    },
    {
      path: "/admin",
      name: "admin",
      component: () => import('./views/AdminView.vue'),
      meta: { requiresAuth: true, transition: 'none' },
      redirect: { name: 'admin-users' },
      children: [
        {
          path: "users",
          name: "admin-users",
          component: () => import('./views/AdminUsersView.vue'),
          meta: { transition: 'none' },
        },
        {
          path: "pieces",
          name: "admin-pieces",
          component: () => import('./views/AdminPiecesView.vue'),
          meta: { transition: 'none' },
        },
        {
          path: "difficulties",
          name: "admin-difficulties",
          component: () => import('./views/AdminDifficultiesView.vue'),
          meta: { transition: 'none' },
        },
        {
          path: "lessons",
          name: "admin-lessons",
          component: () => import('./views/AdminLessonsView.vue'),
          meta: { transition: 'none' },
        },
        {
          path: "lessons/:lessonId/edit",
          name: "admin-lesson-edit",
          component: () => import('./views/LessonEditView.vue'),
          meta: { transition: 'none', backTo: { name: 'admin-lessons' } },
        },
        {
          path: "pieces/:pieceId/edit",
          name: "admin-piece-edit",
          component: () => import('./views/PieceEditView.vue'),
          meta: { transition: 'none', backTo: pieceBackTarget },
        },
        {
          path: "pieces/create",
          name: "admin-piece-create",
          component: () => import('./views/PieceUploadView.vue'),
          meta: { transition: 'none', backTo: pieceBackTarget },
        },
      ],
    },
    {
      path: "/classroom",
      name: "classroom",
      component: ClassroomView,
      meta: { requiresAuth: true, backTo: { name: 'home' } }
    },
    {
      path: "/classroom/:code",
      name: "classroom-lobby",
      component: ClassroomLobbyView,
      meta: { requiresAuth: true, backTo: { name: 'classroom' } }
    },
    {
      path: "/classroom/:code/play",
      name: "classroom-game",
      component: ClassroomGameView,
      meta: { requiresAuth: true, backTo: classroomGameBackTarget }
    },
    {
      path: '/tearable',
      name: 'tearable',
      component: () => import('./views/TearableView.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: 'home' },
    },
  ],
});

router.beforeEach((to) => {
  // Skip transition on initial page load
  if (isInitialPageLoad) {
    isInitialPageLoad = false;
    to.meta.transition = 'fade';
    return;
  }
});

// Navigation guard for auth-required routes
router.beforeEach(async (to) => {
  // Check if the route requires auth
  if (to.meta.requiresAuth) {
    const user = await getCurrentUser();
    if (!user) {
      isInitialPageLoad = true;
      return {
        path: '/auth',
        query: { redirect: to.fullPath },
      }
    }
  } else if (to.path === '/auth' && to.query.logout !== 'true') {
    const user = await getCurrentUser();
    if (user) {
      return {
        path: '/',
      }
    }
  }
});

const CHUNK_RELOAD_KEY = 'drumblebee:chunk-reload';

// A tab can retain an old app shell after a deploy while its hashed lazy chunk
// has been replaced. Reload once to recover the current shell, but avoid a loop
// if the chunk is still unavailable.
router.onError((error) => {
  const message = error instanceof Error ? error.message : String(error);
  const isDynamicImportFailure =
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed');

  // Route-loading failures can be recovered by a reload and therefore may not
  // reach Vue's global error handler. Capture them before attempting recovery
  // so Android/PWA reports retain the original error and route context.
  Sentry.captureException(error, {
    tags: {
      source: 'router.onError',
      dynamic_import_failure: String(isDynamicImportFailure),
    },
    extra: {
      current_route: router.currentRoute.value.fullPath,
      url: window.location.href,
      chunk_reload_already_attempted: Boolean(sessionStorage.getItem(CHUNK_RELOAD_KEY)),
    },
  });

  if (!isDynamicImportFailure || sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;

  sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  window.location.reload();
});

router.isReady().then(() => {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
});

export default router;
